import type {
  HelmOnboardingProgress as ServerProgress,
  HelmOnboardingProgressUpdate as ServerProgressUpdate,
  HelmOnboardingTourRecord as ServerTourRecord,
} from '../../../lib/api/types'
import { parseProgress, type TourProgress, type TourRecord } from './tour-machine'

/**
 * Tour progress kept on the server, so it follows a person to every device.
 * This browser keeps a copy for when the server can't be reached. These are
 * the pure parts: reading the server's copy, merging two copies, and what changed.
 */

/** The server's progress as the tours keep it, dropping anything malformed. */
export function fromServer(payload: ServerProgress): TourProgress {
  return parseProgress(
    JSON.stringify({
      version: 1,
      offeredAt: payload?.offered_at ?? null,
      tours: Object.fromEntries(
        Object.entries(payload?.tours ?? {}).map(([id, record]) => [
          id,
          {
            completedAt: record?.completed_at ?? undefined,
            skippedAt: record?.skipped_at ?? undefined,
            lastStep: record?.last_step ?? undefined,
          },
        ]),
      ),
    }),
  )
}

const toServerRecord = (record: TourRecord): ServerTourRecord => ({
  completed_at: record.completedAt ?? null,
  skipped_at: record.skippedAt ?? null,
  last_step: record.lastStep ?? null,
})

const same = (a: TourRecord | undefined, b: TourRecord | undefined) =>
  JSON.stringify(toServerRecord(a ?? {})) === JSON.stringify(toServerRecord(b ?? {}))

const earliest = (a?: string | null, b?: string | null) => (!a ? b : !b ? a : a < b ? a : b)
const latest = (a?: string | null, b?: string | null) => (!a ? b : !b ? a : a > b ? a : b)

/**
 * Two copies of someone's progress as one, by the server's rules: a tour once
 * done stays done, the latest skip wins, and Getting started counts as
 * offered from the first time. The step to resume from is this browser's for
 * the tours in `stepsFromMine` (changed here and not yet saved), and the
 * server's for the rest.
 */
export function mergeProgress(
  server: TourProgress,
  mine: TourProgress,
  stepsFromMine: ReadonlySet<string> | 'all',
): TourProgress {
  const tours: Record<string, TourRecord> = {}
  for (const id of new Set([...Object.keys(server.tours), ...Object.keys(mine.tours)])) {
    const theirs = server.tours[id]
    const ours = mine.tours[id]
    const completedAt = earliest(theirs?.completedAt, ours?.completedAt)
    const skippedAt = latest(theirs?.skippedAt, ours?.skippedAt)
    const useMine = ours && (!theirs || stepsFromMine === 'all' || stepsFromMine.has(id))
    const lastStep = useMine ? ours.lastStep : theirs?.lastStep
    tours[id] = {
      ...(completedAt ? { completedAt } : {}),
      ...(skippedAt ? { skippedAt } : {}),
      ...(lastStep !== undefined ? { lastStep } : {}),
    }
  }
  return {
    version: 1,
    offeredAt: earliest(server.offeredAt, mine.offeredAt) ?? null,
    tours,
  }
}

/** What `next` has that the server, holding `saved`, doesn't: null when nothing. */
export function changesFrom(
  saved: TourProgress | null,
  next: TourProgress,
): ServerProgressUpdate | null {
  const update: ServerProgressUpdate = {}
  if (next.offeredAt && next.offeredAt !== saved?.offeredAt) update.offered_at = next.offeredAt
  const tours = Object.entries(next.tours).filter(([id, record]) => !same(saved?.tours[id], record))
  if (tours.length) {
    update.tours = Object.fromEntries(tours.map(([id, record]) => [id, toServerRecord(record)]))
  }
  return update.offered_at || update.tours ? update : null
}
